// ============================================================
//  INSURANCE PAGE — ADMIN DASHBOARD
//  Path: src/pages/InsurancePage.tsx
//
//  Access: super_admin, admin, customer_service (view-only — the review
//  itself happens on the tasker's page and is admin-only on the backend).
//
//  Every uploaded insurance document in one list, mandatory and optional,
//  with the review state and what it means for the tasker's insurance fee:
//    "Insurance fee waived ($0)" — an accepted, valid policy removes the fee
//    "Insurance fee charged"      — pending, rejected, expired or no policy
//
//  This list is separate from approving the person. Reviewing a document
//  here never changes whether the tasker is approved; it only turns that
//  fee off or on. All wording and the fee state come from the backend.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { listInsuranceReviews } from '../services/admin.service'

type ReviewStatus = 'pending' | 'accepted' | 'rejected'
type FeeStatus = 'waived' | 'charged' | 'no_fee'

interface InsuranceRow {
  documentId: string
  expertId: string
  taskerName: string
  taskerEmail: string | null
  isCompany: boolean
  categoryName: string | null
  requirementLabel: string | null
  insuranceType: string | null
  isOptional: boolean
  reviewStatus: ReviewStatus
  rejectionReason: string | null
  fileName: string | null
  uploadedAt: string
  policyProvider: string | null
  policyExpiresAt: string | null
  feeStatus: FeeStatus
  feeLabel: string
}

const FILTERS: { value: 'pending' | 'accepted' | 'rejected' | 'all'; label: string }[] = [
  { value: 'pending',  label: 'Waiting for review' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all',      label: 'All' },
]

const INSURANCE_TYPE_NAMES: Record<string, string> = {
  liability: 'Liability',
  cargo: 'Cargo',
  commercial_auto: 'Commercial auto',
  professional_indemnity: 'Professional liability (E&O)',
}

const REVIEW_COLORS: Record<ReviewStatus, string> = {
  pending:  'bg-yellow-600/20 text-yellow-400',
  accepted: 'bg-green-600/20 text-green-400',
  rejected: 'bg-red-600/20 text-red-400',
}
const REVIEW_LABELS: Record<ReviewStatus, string> = {
  pending: 'Waiting for review', accepted: 'Accepted', rejected: 'Rejected',
}
const FEE_COLORS: Record<FeeStatus, string> = {
  waived:  'bg-green-600/20 text-green-400',
  charged: 'bg-orange-600/20 text-orange-400',
  no_fee:  'bg-gray-600/20 text-gray-400',
}

const PAGE_SIZE = 20

const formatDate = (iso: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString()
}

const InsurancePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const urlStatus = searchParams.get('status') || 'pending'
  const status = FILTERS.some((f) => f.value === urlStatus)
    ? (urlStatus as 'pending' | 'accepted' | 'rejected' | 'all')
    : 'pending'
  const setStatus = (value: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('status', value)
    setSearchParams(next, { replace: true })
  }

  const [rows, setRows]       = useState<InsuranceRow[]>([])
  const [total, setTotal]     = useState(0)
  const [page, setPage]       = useState(1)
  const [search, setSearch]   = useState('')
  const [query, setQuery]     = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState('')

  const fetchRows = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await listInsuranceReviews({ page, limit: PAGE_SIZE, status, search: query || undefined })
      setRows(res.data.data)
      setTotal(res.data.pagination.total)
    } catch (err: any) {
      console.error('Failed to fetch insurance documents:', err)
      setError(err?.response?.data?.message || (err?.isNetworkError
        ? 'Could not reach the server — check your connection and try again.'
        : 'Failed to load insurance documents'))
    } finally {
      setLoading(false)
    }
  }, [page, status, query])

  useEffect(() => { fetchRows() }, [fetchRows])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-2xl font-bold text-white">Insurance</h1>
          <p className="text-gray-400 text-sm mt-1">{total} document{total === 1 ? '' : 's'}</p>
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-6 max-w-3xl">
        Reviewing insurance here never changes whether a tasker is approved. An accepted document
        removes the insurance fee ($0) on that tasker's jobs in the category; a rejected or missing one
        leaves the fee in place. Open the tasker to accept or reject the document.
      </p>

      <div className="flex flex-wrap gap-3 mb-6">
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1) }}
          className="bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white focus:border-brand-500 focus:outline-none"
        >
          {FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <form
          onSubmit={(e) => { e.preventDefault(); setQuery(search.trim()); setPage(1) }}
          className="flex gap-2"
        >
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tasker name or email"
            className="bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none w-72"
          />
          <button type="submit" className="px-4 py-2.5 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light">
            Search
          </button>
        </form>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 flex items-center justify-between gap-3">
          <p className="text-red-400 text-sm">{error}</p>
          <button onClick={() => fetchRows()}
            className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors shrink-0">
            Try again
          </button>
        </div>
      )}

      <div className="bg-surface-card border border-border-light rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-light">
              {['Tasker', 'Category', 'Insurance', 'Required?', 'Review', 'Insurance fee', 'Expires', 'Uploaded', 'Actions'].map((h) => (
                <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-8 text-gray-400">Loading...</td></tr>
            ) : error ? (
              <tr><td colSpan={9} className="text-center py-8 text-gray-500">—</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-8 text-gray-400">No insurance documents found</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.documentId} className="border-b border-border-light hover:bg-surface-tertiary/50 transition-colors">
                  <td className="px-5 py-4">
                    <p className="text-sm font-medium text-white">
                      {r.taskerName}
                      {r.isCompany && <span className="ml-2 text-xs text-purple-400">🏢 Company</span>}
                    </p>
                    {r.taskerEmail && <p className="text-xs text-gray-500">{r.taskerEmail}</p>}
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-300">{r.categoryName || '—'}</td>
                  <td className="px-5 py-4 text-sm text-gray-300">
                    {r.insuranceType ? (INSURANCE_TYPE_NAMES[r.insuranceType] || r.insuranceType) : '—'}
                    {r.requirementLabel && <p className="text-xs text-gray-500">{r.requirementLabel}</p>}
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <span className={r.isOptional ? 'text-green-400' : 'text-gray-300'}>
                      {r.isOptional ? 'Optional' : 'Required'}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${REVIEW_COLORS[r.reviewStatus]}`}>
                      {REVIEW_LABELS[r.reviewStatus]}
                    </span>
                    {r.rejectionReason && <p className="text-xs text-gray-500 mt-1 max-w-[200px]">{r.rejectionReason}</p>}
                  </td>
                  <td className="px-5 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${FEE_COLORS[r.feeStatus]}`}>{r.feeLabel}</span>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-300">
                    {r.policyExpiresAt ? formatDate(r.policyExpiresAt) : (r.reviewStatus === 'accepted' ? 'No expiry' : '—')}
                    {r.policyProvider && <p className="text-xs text-gray-500">{r.policyProvider}</p>}
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-400">{formatDate(r.uploadedAt)}</td>
                  <td className="px-5 py-4">
                    <Link to={`/experts/${r.expertId}`} className="text-sm text-brand-400 hover:text-brand-300">
                      Open
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-border-light">
            <p className="text-sm text-gray-400">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-30 hover:bg-border-light"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 text-sm bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-30 hover:bg-border-light"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default InsurancePage